/**
 * PWA Storage Utilities
 * 
 * Provides localStorage helpers and detection utilities for PWA functionality.
 * Handles storage operations safely with SSR compatibility.
 */

// Storage Keys Constants
export const STORAGE_KEYS = {
  DISMISSED_INSTALL: 'pwa_dismissed_install_prompt',
  DISMISSED_IOS_GUIDE: 'pwa_dismissed_ios_guide',
  COOKIE_CONSENT: 'cookie_consent_status',
  POSTHOG_PENDING_IDENTIFICATION: 'posthog_pending_identification',
  CURRENCY_PREFERENCE: 'currency_preference',
  /** Se descartó el aviso de versión en otro idioma (ver lib/locale-suggestion). */
  DISMISSED_LOCALE_SUGGESTION: 'dismissed_locale_suggestion',
  /** Último toque de marketing antes de comprar (ver lib/marketing-attribution). Sólo con consentimiento. */
  MARKETING_ATTRIBUTION: 'marketing_attribution',
  /** Alta en curso hasta el sync de cuenta (ver lib/pending-signup). */
  PENDING_SIGNUP: 'papanoelchat_pending_signup',
  /** De dónde vino el padre que se registra (ver lib/acquisition-capture, DD17). */
  ACQUISITION_SOURCE: 'papanoelchat_acquisition_source',
  /** El nombre del niño que pasa del chat público al alta, con su fecha (ver lib/public-chat/child-name). */
  PUBLIC_CHAT_CHILD_NAME: 'santa-public-chat:child-name',
  /** Día UTC en que este navegador abrió el chat público (DD3; ver components/public-chat/public-chat-storage). */
  PUBLIC_CHAT_DEVICE_USED_ON: 'public-santa-chat:device-used-on',
} as const

// Type for storage keys
export type StorageKey = typeof STORAGE_KEYS[keyof typeof STORAGE_KEYS]

/**
 * Check if localStorage is available (client-side only)
 */
function isLocalStorageAvailable(): boolean {
  if (typeof window === 'undefined') return false
  
  try {
    const test = '__localStorage_test__'
    window.localStorage.setItem(test, test)
    window.localStorage.removeItem(test)
    return true
  } catch {
    return false
  }
}

/**
 * Get item from localStorage
 * Returns null if not available or key doesn't exist
 */
export function getStorageItem(key: StorageKey): string | null {
  if (!isLocalStorageAvailable()) return null
  
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

/**
 * Set item in localStorage
 * Returns true if successful, false otherwise
 */
export function setStorageItem(key: StorageKey, value: string): boolean {
  if (!isLocalStorageAvailable()) return false
  
  try {
    window.localStorage.setItem(key, value)
    return true
  } catch {
    return false
  }
}

/**
 * Remove item from localStorage
 * Returns true if successful, false otherwise
 */
export function removeStorageItem(key: StorageKey): boolean {
  if (!isLocalStorageAvailable()) return false
  
  try {
    window.localStorage.removeItem(key)
    return true
  } catch {
    return false
  }
}

/**
 * Get boolean value from localStorage
 * Returns false if not available or not set to 'true'
 */
export function getStorageBoolean(key: StorageKey): boolean {
  const value = getStorageItem(key)
  return value === 'true'
}

/**
 * Set boolean value in localStorage
 */
export function setStorageBoolean(key: StorageKey, value: boolean): boolean {
  return setStorageItem(key, value ? 'true' : 'false')
}

/**
 * Get item from sessionStorage
 * Returns null if not available or key doesn't exist
 */
export function getSessionItem(key: string): string | null {
  if (typeof window === 'undefined') return null
  
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

/**
 * Set item in sessionStorage
 * Returns true if successful, false otherwise
 */
export function setSessionItem(key: string, value: string): boolean {
  if (typeof window === 'undefined') return false
  
  try {
    window.sessionStorage.setItem(key, value)
    return true
  } catch {
    return false
  }
}

/**
 * Check if running in Capacitor native app
 */
export function isCapacitor(): boolean {
  if (typeof window === 'undefined') return false

  // @capacitor/core inyecta `window.Capacitor` también en web (shim con
  // isNativePlatform() === false). Solo es app native si lo confirma la API.
  const cap = (window as Window & { Capacitor?: { isNativePlatform: () => boolean } }).Capacitor
  return cap?.isNativePlatform() === true
}

/**
 * Check if running on iOS
 */
export function isIOS(): boolean {
  if (typeof window === 'undefined') return false
  
  const userAgent = window.navigator.userAgent.toLowerCase()
  return /iphone|ipad|ipod/.test(userAgent)
}

/**
 * Check if running in Safari browser
 */
export function isSafari(): boolean {
  if (typeof window === 'undefined') return false
  
  const userAgent = window.navigator.userAgent.toLowerCase()
  const isSafariBrowser = /safari/.test(userAgent) && !/chrome|crios|fxios/.test(userAgent)
  
  return isSafariBrowser
}

/**
 * Check if running in Safari on iOS
 * This is the target for iOS install guide
 */
export function isIOSSafari(): boolean {
  return isIOS() && isSafari()
}

/**
 * Check if running as standalone PWA (installed)
 */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  
  // Check if running in Capacitor (native app)
  if (isCapacitor()) return true
  
  // Check iOS standalone mode
  if ('standalone' in window.navigator) {
    return (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  }
  
  // Check display-mode media query (Android/Desktop)
  if (window.matchMedia) {
    return window.matchMedia('(display-mode: standalone)').matches
  }
  
  return false
}

/**
 * Check if browser supports beforeinstallprompt event
 * (Chrome, Edge, Samsung Internet on Android/Desktop)
 */
export function supportsInstallPrompt(): boolean {
  if (typeof window === 'undefined') return false
  
  return 'onbeforeinstallprompt' in window
}

/**
 * Get platform type
 */
export function getPlatform(): 'ios' | 'android' | 'web' | 'capacitor' {
  if (isCapacitor()) return 'capacitor'
  if (isIOS()) return 'ios'
  
  // Check for Android
  if (typeof window !== 'undefined') {
    const userAgent = window.navigator.userAgent.toLowerCase()
    if (/android/.test(userAgent)) return 'android'
  }
  
  return 'web'
}

/**
 * Check if should show install prompt
 * Returns false if already installed, dismissed, or not supported
 */
export function shouldShowInstallPrompt(): boolean {
  // Don't show if already installed
  if (isStandalone()) return false
  
  // Don't show on iOS (use iOS guide instead)
  if (isIOSSafari()) return false
  
  // Don't show if browser doesn't support it
  if (!supportsInstallPrompt()) return false
  
  // Don't show if dismissed permanently
  if (getStorageBoolean(STORAGE_KEYS.DISMISSED_INSTALL)) return false
  
  return true
}

/**
 * Check if should show iOS install guide
 * Returns false if already installed, dismissed, or not iOS Safari
 */
export function shouldShowIOSGuide(): boolean {
  // Only show on iOS Safari
  if (!isIOSSafari()) return false
  
  // Don't show if already installed
  if (isStandalone()) return false
  
  // Don't show if dismissed
  if (getStorageBoolean(STORAGE_KEYS.DISMISSED_IOS_GUIDE)) return false
  
  return true
}

